import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { services, testimonials } from '../mock';
import { Smartphone, Laptop, Monitor, Code, Cpu, Wrench, Star, MessageCircle, ArrowRight, Megaphone } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Smartphone,
  Laptop,
  Monitor,
  Code,
  Cpu,
  Wrench
};

export const Home = () => {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [showArrow, setShowArrow] = useState(true);
  const [visitCount, setVisitCount] = useState(5000);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addToCart } = useCart();

  useEffect(() => {
    fetchPromotions();
    fetchProducts();
    incrementVisitCount();

    // Scroll listener para ocultar la flecha
    const handleScroll = () => {
      const promotionsSection = document.getElementById('promotions-section');
      if (promotionsSection) {
        const rect = promotionsSection.getBoundingClientRect();
        // Ocultar flecha cuando la sección de promociones está visible
        setShowArrow(rect.top > window.innerHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await fetch(`${API}/promotions?active_only=true`);
      if (response.ok) {
        const data = await response.json();
        setPromotions(data);
      }
    } catch (error) {
      console.error('Error loading promotions:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const incrementVisitCount = async () => {
    try {
      const response = await fetch(`${API}/visits/increment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setVisitCount(data.count);
      }
    } catch (error) {
      console.error('Error incrementing visit count:', error);
    }
  };

  const addToCartFromPromo = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) {
      toast({
        title: "Error",
        description: "Producto no encontrado",
        variant: "destructive"
      });
      return;
    }

    addToCart(product);

    toast({
      title: "¡Producto agregado!",
      description: `${product.name} se agregó al carrito`
    });

    // Redirect to cart
    navigate('/cart');
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/543586019885', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative px-4 py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-50"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          {/* Visit Counter */}
          <div className="flex justify-end mb-4">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-full px-6 py-2 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                <span className="text-slate-400 text-sm font-medium">Visitas</span>
              </div>
              <span className="text-cyan-400 font-bold text-lg tabular-nums">
                {visitCount.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Reparación Profesional de Tecnología
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Especialistas en celulares, notebooks y computadoras. Desde hardware hasta microelectrónica.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
                onClick={handleWhatsAppClick}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Contactar por WhatsApp
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 text-lg px-8 py-6 transition-all duration-300"
                asChild
              >
                <Link to="/services">Ver Servicios</Link>
              </Button>
            </div>
          </div>

          {/* Flecha indicadora hacia promociones */}
          {showArrow && promotions.length > 0 && (
            <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
              <div className="flex flex-col items-center cursor-pointer" onClick={() => {
                document.getElementById('promotions-section')?.scrollIntoView({ behavior: 'smooth' });
              }}>
                <p className="text-cyan-400 font-bold text-lg mb-2 animate-pulse">
                  ¡Ver Promociones! 🎉
                </p>
                <svg className="w-12 h-12 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                <svg className="w-12 h-12 text-emerald-400 -mt-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Promotions Section */}
      {promotions.length > 0 && (
        <section id="promotions-section" className="px-4 py-20 bg-gradient-to-br from-cyan-900/30 via-slate-900 to-emerald-900/30 relative overflow-hidden scroll-mt-20">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-emerald-400 to-cyan-400 bg-clip-text text-transparent animate-pulse">
                🎉 Promociones Especiales 🎉
              </h2>
              <p className="text-slate-300 text-xl md:text-2xl">¡Aprovechá nuestras ofertas exclusivas!</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {promotions.map((promo) => (
                <Card 
                  key={promo.id} 
                  className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl border-2 border-cyan-500/50 hover:border-cyan-400 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-cyan-500/50 overflow-hidden group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {promo.image_url && (
                    <div className="h-56 overflow-hidden relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent z-10"></div>
                      <img 
                        src={promo.image_url} 
                        alt={promo.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold text-sm animate-bounce z-20">
                        ¡OFERTA!
                      </div>
                    </div>
                  )}
                  
                  <CardHeader className="relative z-10">
                    <CardTitle 
                      className={`${promo.font_size || 'text-3xl'} font-bold leading-tight`}
                      style={{ 
                        fontFamily: promo.font_family || 'Inter',
                        color: promo.text_color || '#ffffff'
                      }}
                    >
                      {promo.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="relative z-10">
                    <p className="text-slate-300 text-lg mb-6 leading-relaxed">
                      {promo.description}
                    </p>
                    {(promo.link || promo.promo_type === 'producto') && (
                      <div className="relative">
                        <Button 
                          className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 w-full text-lg py-6 font-bold shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 animate-pulse hover:animate-none relative overflow-hidden group"
                          onClick={() => {
                            if (promo.promo_type === 'producto' && promo.product_id) {
                              // Agregar producto al carrito y redirigir
                              addToCartFromPromo(promo.product_id);
                            } else if (promo.link) {
                              if (promo.link.startsWith('/')) {
                                navigate(promo.link);
                              } else {
                                window.open(promo.link, '_blank');
                              }
                            }
                          }}
                        >
                          <span className="absolute -left-2 top-1/2 transform -translate-y-1/2 text-3xl animate-bounce">
                            👉
                          </span>
                          {promo.promo_type === 'producto' ? (
                            <span className="ml-8">¡COMPRAR AHORA! <ArrowRight className="ml-2 h-5 w-5 inline" /></span>
                          ) : (
                            <span className="ml-8">¡QUIERO ESTA OFERTA! <ArrowRight className="ml-2 h-5 w-5 inline" /></span>
                          )}
                          <span className="absolute -right-2 top-1/2 transform -translate-y-1/2 text-3xl animate-bounce">
                            👈
                          </span>
                        </Button>
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-2xl animate-bounce">
                          👇
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Preview */}
      <section className="px-4 py-20 bg-slate-900/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Nuestros Servicios</h2>
            <p className="text-slate-400 text-lg">Soluciones completas para todos tus dispositivos</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 group">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-6 w-6 text-cyan-400" />
                    </div>
                    <CardTitle className="text-white">{service.title}</CardTitle>
                    <CardDescription className="text-slate-400">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature) => (
                        <li key={`${service.title}-${feature}`} className="text-slate-300 text-sm flex items-center">
                          <ArrowRight className="h-4 w-4 text-cyan-400 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
              asChild
            >
              <Link to="/services">Ver Todos los Servicios</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-4 py-20">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Lo Que Dicen Nuestros Clientes</h2>
            <p className="text-slate-400 text-lg">Confianza y calidad en cada reparación</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
                <CardHeader>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.id}-${i}`} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardTitle className="text-white text-lg">{testimonial.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">{testimonial.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-20 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">¿Listo para Reparar tu Dispositivo?</h2>
            <p className="text-slate-300 text-lg mb-8">
              Contactanos ahora y recibí un diagnóstico gratuito. Respuesta rápida por WhatsApp.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white text-lg px-12 py-6 transition-all duration-300 hover:scale-105"
              onClick={handleWhatsAppClick}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Contactar Ahora
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;